import { Formats, TranslationValues, useTranslations } from "next-intl";
import { IntlMessageNamespace } from "../types/i18n";
import { snakeToCamel, snakeToPascal } from "@utils/misc";

type TUseNormaliseTranslations = (
  key: string,
  values?: TranslationValues,
  formats?: Formats,
) => string;

/**
 * Maps a dot-separated key to the shape expected in message files.
 *
 * - Default: only the last segment is camelCased; earlier segments are unchanged.
 *   Use when parents are already correct (e.g. `Foo.bar_baz` → `Foo.barBaz`).
 * - normaliseAllKeys: every segment is camelCased. Fine when all groups are camelCase;
 *   wrong when message files use PascalCase groups (e.g. `KidneyDisease`).
 * - pascalParentsCamelLeaf: each parent segment is PascalCase from snake_case / UPPER_SNAKE;
 *   the leaf is camelCased. Matches enums like `KIDNEY_DISEASE.title` → `KidneyDisease.title`
 *   and `CLUCK.imageBadge` → `Cluck.imageBadge`.
 */
export const normaliseKey = (
  key: string,
  normaliseAllKeys = false,
  pascalParentsCamelLeaf = false,
): string => {
  if (!key) { return ""; }

  const parts = key.split(".");

  if (pascalParentsCamelLeaf) {
    const last = parts.pop();
    if (last === undefined) { return ""; }
    const leaf = snakeToCamel(last);
    const parents = parts.map(snakeToPascal);
    return [ ...parents, leaf ].join(".");
  }

  if (normaliseAllKeys) {
    return parts.map(snakeToCamel).join(".");
  }

  const lastPart = parts.pop();
  const camel = lastPart === undefined ? "" : snakeToCamel(lastPart);

  return [ ...parts, camel ].join(".");
};

export function useNormaliseTranslations<NS extends IntlMessageNamespace>(
  namespace: NS,
  normaliseAllKeys = false,
  pascalParentsCamelLeaf = false,
): TUseNormaliseTranslations {
  const t = useTranslations(namespace);
  return (key, values, formats) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    t(normaliseKey(key, normaliseAllKeys, pascalParentsCamelLeaf) as any, values, formats);
}
